import React, { useState, useEffect } from "react";
import analyticsService from "../../services/analyticsService";
import "./LogViewer.css";

function LogViewer() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalLogs: 0,
    limit: 50,
  });
  const [stats, setStats] = useState({});
  const [statusBreakdown, setStatusBreakdown] = useState({});
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    logType: "all",
    status: "all",
    userId: "",
    search: "",
    dateFrom: "",
    dateTo: "",
    sortBy: "createdAt",
    sortOrder: "desc",
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, [filters.page, filters.limit, filters.logType, filters.status, filters.sortBy, filters.sortOrder]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = { ...filters };

      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === "all") {
          delete params[key];
        }
      });

      const response = await analyticsService.getLogs(params);

      if (response.success && response.data) {
        setLogs(response.data.logs);
        setPagination(response.data.pagination);
        setStats(response.data.stats);
        setStatusBreakdown(response.data.statusBreakdown);
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key !== "page" ? 1 : value,
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, page: 1 }));
    fetchLogs();
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      handleFilterChange("page", newPage);
    }
  };

  const openDetailModal = (log) => {
    setSelectedLog(log);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedLog(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Dhaka",
    });
  };

  const getLogIcon = (logType) => {
    const icons = {
      sms: "sms",
      push: "notifications",
      admin_action: "admin_panel_settings",
      user_activity: "person",
      system_event: "settings_suggest",
      error: "error",
    };
    return icons[logType] || "description";
  };

  const getStatusBadge = (status) => {
    const badges = {
      success: { class: "status-success", icon: "check_circle" },
      failed: { class: "status-failed", icon: "cancel" },
      pending: { class: "status-pending", icon: "schedule" },
    };
    return badges[status] || { class: "", icon: "help" };
  };

  return (
    <div className="lv-container">
      {/* Header */}
      <div className="lv-header">
        <h1>System Logs</h1>
        <p>View and analyze system logs</p>
      </div>

      {/* Stats */}
      <div className="lv-stats">
        <div className="lv-stat">
          <span className="material-symbols-outlined">sms</span>
          <div><small>SMS</small><strong>{stats.sms || 0}</strong></div>
        </div>
        <div className="lv-stat">
          <span className="material-symbols-outlined">notifications</span>
          <div><small>Push</small><strong>{stats.push || 0}</strong></div>
        </div>
        <div className="lv-stat">
          <span className="material-symbols-outlined">admin_panel_settings</span>
          <div><small>Admin</small><strong>{stats.admin_action || 0}</strong></div>
        </div>
        <div className="lv-stat">
          <span className="material-symbols-outlined">error</span>
          <div><small>Errors</small><strong>{stats.error || 0}</strong></div>
        </div>
        <div className="lv-stat lv-stat-success">
          <span className="material-symbols-outlined">check_circle</span>
          <div><small>Success</small><strong>{statusBreakdown.success || 0}</strong></div>
        </div>
        <div className="lv-stat lv-stat-failed">
          <span className="material-symbols-outlined">cancel</span>
          <div><small>Failed</small><strong>{statusBreakdown.failed || 0}</strong></div>
        </div>
      </div>

      {/* Filters */}
      <div className="lv-filters">
        <form onSubmit={handleSearch}>
          <div className="lv-filter-row">
            <select value={filters.logType} onChange={(e) => handleFilterChange("logType", e.target.value)}>
              <option value="all">All Types</option>
              <option value="sms">SMS</option>
              <option value="push">Push</option>
              <option value="admin_action">Admin</option>
              <option value="user_activity">Activity</option>
              <option value="system_event">System</option>
              <option value="error">Errors</option>
            </select>

            <select value={filters.status} onChange={(e) => handleFilterChange("status", e.target.value)}>
              <option value="all">All Status</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
            </select>

            <select value={filters.sortOrder} onChange={(e) => handleFilterChange("sortOrder", e.target.value)}>
              <option value="desc">Newest</option>
              <option value="asc">Oldest</option>
            </select>

            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
              placeholder="From"
            />

            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange("dateTo", e.target.value)}
              placeholder="To"
            />

            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              placeholder="Search..."
            />

            <button type="submit">
              <span className="material-symbols-outlined">search</span>
            </button>
          </div>
        </form>
      </div>

      {/* Table */}
      <div className="lv-table-container">
        {loading ? (
          <div className="lv-loading">Loading...</div>
        ) : logs.length === 0 ? (
          <div className="lv-empty">
            <span className="material-symbols-outlined">inbox</span>
            <p>No logs found</p>
          </div>
        ) : (
          <table className="lv-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Status</th>
                <th>Message</th>
                <th>User ID</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const statusBadge = getStatusBadge(log.status);
                return (
                  <tr key={log._id}>
                    <td>
                      <div className="lv-type">
                        <span className="material-symbols-outlined">{getLogIcon(log.logType)}</span>
                        <span>{log.logType}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`lv-status ${statusBadge.class}`}>
                        <span className="material-symbols-outlined">{statusBadge.icon}</span>
                      </span>
                    </td>
                    <td className="lv-message">{log.message || "-"}</td>
                    <td className="lv-userid">{log.userId || "-"}</td>
                    <td className="lv-date">{formatDate(log.createdAt)}</td>
                    <td>
                      <button className="lv-view-btn" onClick={() => openDetailModal(log)}>
                        <span className="material-symbols-outlined">visibility</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && logs.length > 0 && (
        <div className="lv-pagination">
          <div className="lv-pagination-info">
            {((pagination.currentPage - 1) * pagination.limit) + 1} - {Math.min(pagination.currentPage * pagination.limit, pagination.totalLogs)} of {pagination.totalLogs}
          </div>
          <div className="lv-pagination-controls">
            <button onClick={() => handlePageChange(1)} disabled={pagination.currentPage === 1}>
              <span className="material-symbols-outlined">first_page</span>
            </button>
            <button onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={pagination.currentPage === 1}>
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <span className="lv-current-page">{pagination.currentPage} / {pagination.totalPages}</span>
            <button onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={pagination.currentPage === pagination.totalPages}>
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
            <button onClick={() => handlePageChange(pagination.totalPages)} disabled={pagination.currentPage === pagination.totalPages}>
              <span className="material-symbols-outlined">last_page</span>
            </button>
          </div>
          <select value={filters.limit} onChange={(e) => handleFilterChange("limit", parseInt(e.target.value))}>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedLog && (
        <div className="lv-modal-overlay" onClick={closeDetailModal}>
          <div className="lv-modal" onClick={(e) => e.stopPropagation()}>
            <div className="lv-modal-header">
              <h3>Log Details</h3>
              <button onClick={closeDetailModal}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="lv-modal-body">
              <div className="lv-detail-section">
                <p><strong>Type:</strong> {selectedLog.logType}</p>
                <p><strong>Status:</strong> <span className={`lv-status ${getStatusBadge(selectedLog.status).class}`}><span className="material-symbols-outlined">{getStatusBadge(selectedLog.status).icon}</span></span></p>
                {selectedLog.userId && <p><strong>User ID:</strong> {selectedLog.userId}</p>}
                {selectedLog.message && <p><strong>Message:</strong> {selectedLog.message}</p>}
                <p><strong>Created:</strong> {formatDate(selectedLog.createdAt)}</p>
              </div>

              {selectedLog.logType === "sms" && selectedLog.smsData && (
                <div className="lv-detail-section">
                  <h4>SMS Details</h4>
                  <p><strong>Mobile:</strong> {selectedLog.smsData.mobile}</p>
                  <p><strong>Content:</strong> {selectedLog.smsData.content}</p>
                  <p><strong>Provider:</strong> {selectedLog.smsData.provider}</p>
                </div>
              )}

              {selectedLog.logType === "push" && selectedLog.pushData && (
                <div className="lv-detail-section">
                  <h4>Push Details</h4>
                  <p><strong>Title:</strong> {selectedLog.pushData.title}</p>
                  <p><strong>Body:</strong> {selectedLog.pushData.body}</p>
                  <p><strong>Type:</strong> {selectedLog.pushData.notificationType}</p>
                </div>
              )}

              {selectedLog.logType === "admin_action" && selectedLog.adminData && (
                <div className="lv-detail-section">
                  <h4>Admin Action</h4>
                  <p><strong>Admin:</strong> {selectedLog.adminData.adminName}</p>
                  <p><strong>Action:</strong> {selectedLog.adminData.action}</p>
                  {selectedLog.adminData.targetUserId && <p><strong>Target:</strong> {selectedLog.adminData.targetUserId}</p>}
                </div>
              )}

              {selectedLog.logType === "error" && selectedLog.errorData && (
                <div className="lv-detail-section lv-error">
                  <h4>Error</h4>
                  <p><strong>Type:</strong> {selectedLog.errorData.errorType}</p>
                  <p><strong>Message:</strong> {selectedLog.errorData.errorMessage}</p>
                  {selectedLog.errorData.endpoint && <p><strong>Endpoint:</strong> {selectedLog.errorData.endpoint}</p>}
                </div>
              )}

              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div className="lv-detail-section">
                  <details>
                    <summary>Metadata</summary>
                    <pre>{JSON.stringify(selectedLog.metadata, null, 2)}</pre>
                  </details>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LogViewer;
